/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-sm-study-members.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState, useContext } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useAuthorization, useBoolean, useTranslation, useGridCells } from 'hooks';
import { OrganisationMemberFields, StudyFields } from 'queries/generated/fragments.generated';
import { GET_RESTRICTED_STUDY } from 'queries/studies';
import NotificationService from 'utils/notification';
import AddMemberDialog from 'views/components/admin/add-member-dialog';
import EditUserDialog from 'views/components/admin/edit-user-dialog';
import Button from 'views/components/core/buttons/button';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';

import '../admin-studies-main-styles.scss';

const AdminSmStudyMembers = ({ study }: { study: StudyFields }) => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const canEdit = user?.canWriteSystemSmAdminLevel(false, true);
    const canRead = user.canReadSmStudyAdminLevel();

    const saving = useBoolean(false);
    const showAddMember = useBoolean(false);
    const showEditUser = useBoolean(false);
    const [currentMember, setCurrentMember] = useState(null);
    const [currantEmail, setCurrentEmail] = useState(null);
    const [members, setMembers] = useState([]);

    const { YesNoCell, DateCell, MaxLengthCell } = useGridCells();

    const [loadStudyData, { loading: loadingStudy, error, data: studyData }] = useLazyQuery(GET_RESTRICTED_STUDY);

    const restructuredMembers = (items: any): unknown[] => {
        if (!items) return [];

        return items?.map((item: any) => ({
            ...item,
            _isEnabledUser: !!item.user,
        }));
    };

    useEffect(() => {
        if (studyData?.study) {
            let array = [...studyData.study.smMembers];
            let newData = restructuredUpdatedCreatedBy(array);
            newData = restructuredMembers(newData);
            array = orderBy(newData, [
                { field: 'active', dir: 'desc' },
                { field: '_isEnabledUser', dir: 'desc' },
                { field: 'lastName', dir: 'asc' },
            ]);
            setMembers(array);
            // dispatch({ type: 'SELECT_STUDY', study: {
            //     ...state.selectedStudy,
            //     members: studyData.study.members
            // }});
        }
    }, [studyData]);

    useEffect(() => {
        if (study) loadStudyData({ variables: { id: study.id } }).catch((err) => window.console.error(err));
    }, [study]);

    useEffect(() => {
        if (!showEditUser.value)
            setTimeout(() => {
                setCurrentMember(null);
                setCurrentEmail(null);
            }, 600); // Waiting for an animation
    }, [showEditUser]);

    if (!user) return null;

    if (!canRead) return <h1>You do not have access to this module.</h1>;

    if (!study) return <h3>{t('common:noStudySelected')}</h3>;

    if (loadingStudy && !study) return <Loading />;

    if (error) return <Error error={error} />;

    const actions = (member: OrganisationMemberFields) => [
        {
            label: 'uam:editUser',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentMember(member);
                showEditUser.setTrue();
            },
            enabled: canEdit,
        },
    ];

    return (
        <>
            <EditUserDialog
                studyId={study.id}
                user={currentMember}
                open={showEditUser.value}
                email={currantEmail}
                close={(action) => {
                    if (action === 'assigned')
                        NotificationService.showNotification(
                            t('uam:userHasBeenSuccessfullyAssigned', { studyId: study.recordId }),
                        );
                    if (action === 'edited')
                        NotificationService.showNotification(
                            t('uam:userHasBeenSuccessfullyEdited', { userId: currentMember.recordId }),
                        );
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    showEditUser.setFalse();
                }}
                isSmMember
            />
            <AddMemberDialog
                allMembers={members}
                study={studyData?.study}
                open={showAddMember.value}
                setCurrentEmail={setCurrentEmail}
                close={(action: string) => {
                    if (action === 'assigned')
                        NotificationService.showNotification(
                            t('uam:userHasBeenSuccessfullyAssigned', { studyId: study.recordId }),
                        );
                    if (action === 'add-member') showEditUser.setTrue();
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else showAddMember.setFalse();
                }}
                isSmMember
            />
            <PageTitle text={t('uam:manageSmStudyTeam')} />
            <div className="admin-study-main-container">
                {saving.value && <p className="saving-status">{t('saving')}</p>}
                <div className="admin-study-btns-wrapper">
                    <Button
                        className="save-btn"
                        disabled={saving.value || !canEdit}
                        text="uam:addUser"
                        id="manage-sm-study-team-add-user-btn"
                        onClick={() => {
                            showAddMember.setTrue();
                        }}
                    />
                </div>
                <div>
                    <ExpandableGrid
                        customKey="member-list-grid"
                        id="member-list-grid"
                        key="member-list"
                        items={members}
                        rowActions={actions}
                        groupable={false}
                        showPager
                        scrollable="scrollable"
                        reorderable
                        pageable={false}
                        addScroll
                        resizable
                        options={{
                            allowExcelExport: false,
                            allowExpandCollapseAll: false,
                            useFilterChips: false,
                        }}
                        columns={[
                            {
                                label: t('active'),
                                field: 'active',
                                id: 'member-list-active',
                                lockedByDefault: true,
                                minWidth: 130,
                                cells: { data: YesNoCell },
                                filterUi: GridColumnMenuActiveFilter,
                            },
                            {
                                label: t('opraUser'),
                                field: '_isEnabledUser',
                                id: 'member-list-opraUser',
                                lockedByDefault: true,
                                minWidth: 160,
                                cells: { data: YesNoCell },
                                filterUi: GridColumnMenuActiveFilter,
                            },
                            {
                                label: t('lastName'),
                                field: 'lastName',
                                id: 'member-list-lastName',
                                minWidth: 180,
                                lockedByDefault: true,
                                cells: { data: MaxLengthCell(50) },
                            },
                            {
                                label: t('firstName'),
                                field: 'firstName',
                                id: 'member-list-firstName',
                                minWidth: 180,
                                cells: { data: MaxLengthCell(50) },
                            },
                            {
                                field: 'email',
                                label: t('email'),
                                minWidth: 180,
                                id: 'member-list-email',
                            },
                            {
                                label: t('uam:studyAdministrator'),
                                field: 'isAdmin',
                                id: 'member-list-administrator',
                                minWidth: 230,
                                cells: { data: YesNoCell },
                                filterUi: GridColumnMenuActiveFilter,
                            },
                            {
                                label: t('ra:createdDate'),
                                field: 'created',
                                id: 'member-list-createdDate',
                                minWidth: 200,
                                cells: { data: DateCell },
                                filter: 'date',
                            },
                            {
                                label: t('ra:createdBy'),
                                field: '_createdByString',
                                id: 'member-list-createdBy',
                                minWidth: 200,
                            },
                            {
                                label: t('ra:updatedDate'),
                                field: 'updated',
                                id: 'member-list-updatedDate',
                                minWidth: 200,
                                cells: { data: DateCell },
                                filter: 'date',
                            },
                            {
                                label: t('ra:updatedBy'),
                                field: '_updatedByString',
                                id: 'member-list-updatedBy',
                                minWidth: 200,
                            },
                            {
                                label: 'ID',
                                field: 'recordId',
                                id: 'member-id',
                                minWidth: 180,
                                filter: 'numeric',
                                showByDefault: false,
                            },
                        ]}
                    />
                </div>
            </div>
        </>
    );
};

export default AdminSmStudyMembers;
